
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { COUNTRIES } from "@/lib/countries";

type ProfileFormProps = {
  username: string | null;
  email: string;
  residenceCountry: string | null;
  onUsernameChange: (value: string) => void;
  onCountryChange: (value: string) => void;
};

export const ProfileForm = ({ username, email, residenceCountry, onUsernameChange, onCountryChange }: ProfileFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const updateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) throw new Error(t("profile.messages.notAuthenticated"));

      const { error } = await supabase
        .from("profiles")
        .update({ username, residence_country: residenceCountry })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: t("profile.messages.success"),
        description: t("profile.messages.profileUpdated"),
      });

      // Redirection vers la page principale
      navigate("/");
      
    } catch (error: any) {
      toast({
        title: t("profile.messages.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    navigate("/");
  };

  return (
    <form onSubmit={updateProfile} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="username">{t("profile.form.username")}</Label>
        <Input
          id="username"
          type="text"
          value={username || ""}
          onChange={(e) => onUsernameChange(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="current-email">{t("profile.form.currentEmail")}</Label>
        <Input
          id="current-email"
          type="email"
          value={email}
          disabled
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="residence-country">{t("profile.form.residenceCountry")}</Label>
        <Select value={residenceCountry || ""} onValueChange={onCountryChange}>
          <SelectTrigger id="residence-country">
            <SelectValue placeholder={t("profile.form.selectCountry")} />
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Button type="submit">
          {t("profile.form.updateProfile")}
        </Button>
        <Button type="button" variant="outline" onClick={handleClose}>
          {t("profile.form.close")}
        </Button>
      </div>
    </form>
  );
};